import { ContactPhone } from 'src/components/PhoneInput/PhoneInput';
import { ContactEmail } from 'src/shared/types/User';
import { PhoneRegex } from 'src/utils/constants/common';
import { create } from 'zustand';

interface ProfileInfo {
  id: number;
  relationship: string;
  fullName?: string;
  firstName?: string;
  lastName?: string;
  isPrincipal?: boolean;
}

export enum ProfileActionType {
  UNDO = 'UNDO',
  SAVE = 'SAVE',
}

const IsValidPhoneContact = (
  type: string | undefined,
  countryCode: string | undefined,
  phoneNumber: string | undefined,
  extension: string | undefined,
  isPrimary: boolean | undefined
) => {
  if (!type && !countryCode && !phoneNumber && !extension && !isPrimary) {
    return true;
  }

  const isValidPhoneNumber = phoneNumber && PhoneRegex.test(phoneNumber);
  if (isValidPhoneNumber) {
    return true;
  }

  const isValidCountryCode = countryCode && PhoneRegex.test(countryCode);
  const isValidExtension = extension && PhoneRegex.test(extension);
  return isValidPhoneNumber && (type || isValidCountryCode || isValidExtension || isPrimary);
};

interface UserProfileState {
  displayUpdateButton: boolean;
  setDisplayUpdateButton: (displayUpdateButton: boolean) => void;

  enableSaveChanges: boolean;
  setEnableSaveChanges: (enableSaveChanges: boolean) => void;

  editMode: boolean;
  toggleEditMode: () => void;

  profileInfo: ProfileInfo | null;
  setProfileInfo: (profileInfo: ProfileInfo) => void;

  handleProfileAction: (type: ProfileActionType) => void;
  setProfileActionHandler: (handler: (type: ProfileActionType) => void) => void;

  updatedFields: Record<string, unknown> | null;
  setUpdatedFields: (fields: Record<string, unknown> | null) => void;
}

export const useUserProfileStore = create<UserProfileState>((set) => ({
  displayUpdateButton: true,
  setDisplayUpdateButton: (displayUpdateButton: boolean) => set({ displayUpdateButton }),

  enableSaveChanges: false,
  setEnableSaveChanges: (enableSaveChanges: boolean) => set({ enableSaveChanges }),

  editMode: false,
  toggleEditMode: () => set((state) => ({ editMode: !state.editMode, enableSaveChanges: false })),

  profileInfo: null,
  setProfileInfo: (profileInfo: ProfileInfo) => set({ profileInfo }),

  handleProfileAction: () => {},
  setProfileActionHandler: (handler) => set({ handleProfileAction: handler }),

  updatedFields: null,
  setUpdatedFields: (updatedFields: Record<string, unknown> | null) => {
    set({ updatedFields: updatedFields ? updatedFields : null });
    let enableSave = false;

    if (updatedFields) {
      enableSave = true;

      const basicInformation = updatedFields.basicInformation as Record<string, unknown>;
      if (basicInformation) {
        const requiredFields = ['firstName', 'lastName', 'relationship'];
        const hasRequiredFieldsFilled = requiredFields.every((key) => {
          const hasValue = Boolean(basicInformation[key]);
          return basicInformation.isNew ? hasValue : !basicInformation.hasOwnProperty(key) || hasValue;
        });
        enableSave = Object.keys(basicInformation).length > 0 && hasRequiredFieldsFilled;
      }

      const contactEmails = updatedFields.contactEmails as Record<string, ContactEmail[]>;
      if (contactEmails) {
        if (contactEmails.add?.length > 0) {
          enableSave = contactEmails.add.every((item) => item.isValid);
        }
        if (contactEmails.edit?.length > 0) {
          enableSave = contactEmails.edit.every((item) => item.isValid);
        }
      }

      const contactPhones = updatedFields.contactPhones as Record<string, ContactPhone[]>;
      if (contactPhones) {
        if (contactPhones.add?.length > 0) {
          enableSave =
            enableSave &&
            contactPhones.add.every((item) =>
              IsValidPhoneContact(item.phoneType, item.countryCode, item.phoneNumber, item.extension, item.isPrimary)
            );
        }
        if (contactPhones.edit?.length > 0) {
          enableSave =
            enableSave &&
            contactPhones.edit.every((item) =>
              IsValidPhoneContact(item.phoneType, item.countryCode, item.phoneNumber, item.extension, item.isPrimary)
            );
        }
      }

      if (updatedFields.employmentInformation) {
        enableSave = Object.keys(updatedFields.employmentInformation).length > 0;
      }
    }
    set({ enableSaveChanges: enableSave });
  },
}));
